import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  PaymentMethodDiscountModal,
  PaymentMethodDiscountTable,
  PaymentMethodDiscountViewModal,
} from "../../components/backend/paymentmethoddiscounts";
import {
  usePaymentMethodDiscount,
  usePaymentMethodDiscountMutations,
  usePaymentMethodDiscounts,
} from "../../hooks/usePaymentMethodDiscount";
import {
  CreatePaymentMethodDiscountRequest,
  PaymentMethodDiscount,
  UpdatePaymentMethodDiscountRequest,
} from "../../types/admin";

import AdminPage from "./AdminPage";

type ModalState = {
  open: boolean;
  mode: "create" | "edit";
  discount?: PaymentMethodDiscount;
};

type ViewModalState = {
  open: boolean;
  discountId: string | null;
};

const PaymentMethodDiscountsPage: React.FC = () => {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] =
    useState<PaymentMethodDiscount | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    mode: "create",
  });
  const [viewModalState, setViewModalState] = useState<ViewModalState>({
    open: false,
    discountId: null,
  });
  const [editingDiscountId, setEditingDiscountId] = useState<string | null>(
    null,
  );

  const { data, isLoading } = usePaymentMethodDiscounts();
  const { create, update, remove } = usePaymentMethodDiscountMutations();

  // Fetch full discount details when editing
  const { data: editDiscountData, isLoading: isLoadingEditDiscount } =
    usePaymentMethodDiscount(editingDiscountId || "");

  // Fetch full discount details for view modal
  const { data: viewDiscountData, isLoading: isLoadingViewDiscount } =
    usePaymentMethodDiscount(viewModalState.discountId || "");

  const discounts = useMemo(() => data?.data ?? [], [data]);

  const openCreateModal = () =>
    setModalState({ open: true, mode: "create", discount: undefined });

  const openEditModal = async (discount: PaymentMethodDiscount) => {
    // Set the discount ID to fetch full details
    setEditingDiscountId(discount.id);
    // The modal will open once data is loaded via useEffect
  };

  const openViewModal = (discount: PaymentMethodDiscount) => {
    setViewModalState({ open: true, discountId: discount.id });
  };

  const closeViewModal = () => {
    setViewModalState({ open: false, discountId: null });
  };

  // Open edit modal when full discount data is loaded
  useEffect(() => {
    if (
      editingDiscountId &&
      editDiscountData?.data &&
      !isLoadingEditDiscount
    ) {
      setModalState({
        open: true,
        mode: "edit",
        discount: editDiscountData.data,
      });
      setEditingDiscountId(null); // Reset after opening
    }
  }, [editingDiscountId, editDiscountData, isLoadingEditDiscount]);

  const closeModal = () =>
    setModalState((prev) => ({ ...prev, open: false, discount: undefined }));

  const showMessage = (message: string, severity: AlertColor = "info") =>
    setSnackbar({ open: true, message, severity });

  const handleSave = async (
    payload:
      | CreatePaymentMethodDiscountRequest
      | UpdatePaymentMethodDiscountRequest,
  ) => {
    try {
      if (modalState.mode === "create") {
        await create.mutateAsync(payload as CreatePaymentMethodDiscountRequest);
        showMessage(t("admin.paymentDiscounts.createSuccess"), "success");
      } else if (modalState.discount?.id) {
        await update.mutateAsync({
          id: modalState.discount.id,
          data: payload as UpdatePaymentMethodDiscountRequest,
        });
        showMessage(t("admin.paymentDiscounts.updateSuccess"), "success");
      }
      closeModal();
    } catch (error) {
      console.error("Failed to save payment method discount", error);
      showMessage(t("admin.paymentDiscounts.saveError"), "error");
    }
  };

  const handleDelete = async (discount: PaymentMethodDiscount) => {
    setConfirmDelete(discount);
  };

  const confirmDeletion = async () => {
    if (!confirmDelete) return;
    try {
      await remove.mutateAsync(confirmDelete.id);
      showMessage(t("admin.paymentDiscounts.deleteSuccess"), "success");
      setConfirmDelete(null);
    } catch (error: any) {
      console.error("Failed to delete payment method discount", error);

      // Extract user-friendly error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t("admin.paymentDiscounts.deleteError");

      showMessage(errorMessage, "error");
      setConfirmDelete(null);
    }
  };

  return (
    <AdminPage>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              {t("admin.paymentDiscounts.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("admin.paymentDiscounts.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateModal}
          >
            {t("admin.paymentDiscounts.addDiscount")}
          </Button>
        </Box>

        <PaymentMethodDiscountTable
          discounts={discounts}
          loading={
            isLoading ||
            create.isPending ||
            update.isPending ||
            remove.isPending
          }
          onEdit={openEditModal}
          onDelete={handleDelete}
          onView={openViewModal}
        />

        <PaymentMethodDiscountModal
          open={modalState.open}
          mode={modalState.mode}
          initialData={modalState.discount}
          onClose={closeModal}
          onSave={handleSave}
          submitting={create.isPending || update.isPending}
        />

        <PaymentMethodDiscountViewModal
          open={viewModalState.open}
          discount={viewDiscountData?.data || null}
          loading={isLoadingViewDiscount}
          onClose={closeViewModal}
        />

        <Dialog
          open={Boolean(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t("admin.paymentDiscounts.deleteDialogTitle")}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              {t("admin.paymentDiscounts.deleteConfirmation", { name: confirmDelete?.description })}{" "}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {t("admin.paymentDiscounts.deleteWarning")}
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>{t("admin.paymentDiscounts.cancel")}</Button>
            <Button
              color="error"
              variant="contained"
              onClick={confirmDeletion}
              disabled={remove.isPending}
            >
              {t("admin.paymentDiscounts.delete")}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            variant="filled"
            sx={{ maxWidth: 600 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </AdminPage>
  );
};

export default PaymentMethodDiscountsPage;

